import { type ReconcileSelectionsEvent } from '../events/types';
import { type GridPosition, type SelectionState } from '../state/types';

import { type ReducerResponse } from './types';
import {
  type ReconcileContext,
  reconcileAllForAxis,
  reconcileCellRange,
  reconcileSingleIndex,
} from './utils/reconcile';

/**
 * Reconciles selection ranges after a structural data change (remote Yjs reorder,
 * insertion, or deletion of entries/fields).
 *
 * A `SelectionRange` only stores an anchor and lead index, which implicitly
 * covers every entity between them. When the underlying entity ordering changes
 * the naive approach of reconciling just the two endpoints breaks:
 *
 *  - **Insertion inside a range**: the new entity isn't selected, but the
 *    reconciled endpoints would stretch to include it.
 *  - **Deletion of an endpoint**: the range is dropped even if interior
 *    entities survive.
 *  - **Reorder that breaks contiguity**: the range would span unselected gaps.
 *
 * Instead, this reducer:
 *  1. Expands each range into the full set of entity IDs it covers.
 *  2. Maps each ID to its new index (dropping deleted ones).
 *  3. Groups the new indices into contiguous runs.
 *  4. Emits one `SelectionRange` per contiguous group.
 *
 * For row/column modes, indices are collected across ALL input ranges before
 * grouping, so disjoint ranges that become adjacent after a structural change
 * are naturally merged into a single range.
 */
export function reconcileSelectionsReducer(
  state: SelectionState,
  event: ReconcileSelectionsEvent
): ReducerResponse {
  if (state.mode === 'idle') {
    return { next: state, delta: { kind: 'noop' } };
  }

  const ctx: ReconcileContext = {
    prevEntries: event.entries.prev,
    nextEntriesLookup: new Map(event.entries.next.map((id, i) => [id, i])),
    prevFields: event.fields.prev,
    nextFieldsLookup: new Map(event.fields.next.map((id, i) => [id, i])),
  };

  const selections =
    state.mode === 'row' || state.mode === 'column'
      ? reconcileAllForAxis({ ranges: state.selections, mode: state.mode, ...ctx })
      : state.selections.flatMap((range) => reconcileCellRange({ range, ...ctx }));

  const prevFocusedCell = state.focusedCell;
  let nextFocusedCell: GridPosition | null = null;

  if (prevFocusedCell) {
    const newRow = reconcileSingleIndex(
      prevFocusedCell.row.index,
      ctx.prevEntries,
      ctx.nextEntriesLookup
    );
    const newCol = reconcileSingleIndex(
      prevFocusedCell.col.index,
      ctx.prevFields,
      ctx.nextFieldsLookup
    );

    if (newRow !== null && newCol !== null) {
      nextFocusedCell = {
        row: { index: newRow },
        col: { index: newCol },
      };
    }
  }

  const allSelectionsRemoved = selections.length === 0;
  const nextMode = allSelectionsRemoved ? 'idle' : state.mode;
  const nextFocus = allSelectionsRemoved ? null : nextFocusedCell;

  return {
    next: {
      ...state,
      mode: nextMode,
      selections,
      focusedCell: nextFocus,
      lastInteractionSource: event.source,
    },
    delta: {
      kind: 'selectionReplaced',
      source: event.source,
      selections,
      focusChange: {
        prev: prevFocusedCell,
        next: nextFocus,
      },
    },
  };
}
